import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'
import AXIOS_INSTANCE from '../api/axiosInstance'
import { useProfile } from '../zustand/store'
import { modulesForSku, ProductModuleId, ProductSkuId, PRODUCT_SKUS } from './registry'

type ProductModulesResponse = {
  product_sku: string
  enabled_modules: string[]
  available_modules?: string[]
  available_skus?: string[]
}

const LOCAL_SKU_KEY = 'access_emr_product_sku_override'

export const readLocalSkuOverride = (): ProductSkuId | null => {
  try {
    const raw = localStorage.getItem(LOCAL_SKU_KEY)
    if (raw && raw in PRODUCT_SKUS) return raw as ProductSkuId
  } catch {
    /* ignore */
  }
  return null
}

export const writeLocalSkuOverride = (sku: ProductSkuId | null) => {
  try {
    if (!sku) localStorage.removeItem(LOCAL_SKU_KEY)
    else localStorage.setItem(LOCAL_SKU_KEY, sku)
  } catch {
    /* ignore */
  }
}

export const useProductModules = () => {
  const queryClient = useQueryClient()
  const roles = useProfile(s => s.role) || []
  const isSuperAdmin = roles.map(r => r.toLowerCase()).includes('super_admin')
  const [localSku, setLocalSku] = useState<ProductSkuId | null>(() => readLocalSkuOverride())

  const { data, isLoading, refetch } = useQuery({
    queryFn: async () => {
      try {
        const { data: body } = await AXIOS_INSTANCE.get<ProductModulesResponse>('/product_modules')
        return body
      } catch {
        // Fallback when API is old / offline — still allow local SKU demo
        return {
          product_sku: 'full_hospital_portal',
          enabled_modules: modulesForSku('full_hospital_portal'),
          available_skus: Object.keys(PRODUCT_SKUS)
        } as ProductModulesResponse
      }
    },
    queryKey: ['product_modules'],
    staleTime: 30_000
  })

  const apiProductSku = (data?.product_sku || 'full_hospital_portal') as string
  const apiEnabledModules = useMemo<ProductModuleId[]>(() => {
    const fromApi = (data?.enabled_modules || []).filter(Boolean) as ProductModuleId[]
    if (fromApi.length) return fromApi.includes('core') ? fromApi : (['core', ...fromApi] as ProductModuleId[])
    return modulesForSku('full_hospital_portal')
  }, [data?.enabled_modules])

  const productSku = (localSku || apiProductSku) as string

  const enabledModules = useMemo<ProductModuleId[]>(() => {
    if (localSku) return modulesForSku(localSku)
    return apiEnabledModules
  }, [apiEnabledModules, localSku])

  // Local SKU override = "preview as tenant" (even for super_admin) so you can demo gates.
  const bypassModuleGates = isSuperAdmin && !localSku

  const isModuleEnabled = useCallback(
    (moduleId: ProductModuleId) => {
      if (bypassModuleGates) return true
      return enabledModules.includes(moduleId)
    },
    [bypassModuleGates, enabledModules]
  )

  const setSkuOverride = useCallback(
    async (sku: ProductSkuId, persistToApi = false) => {
      writeLocalSkuOverride(sku)
      setLocalSku(sku)
      if (persistToApi && isSuperAdmin) {
        try {
          await AXIOS_INSTANCE.patch('/product_modules', { product_sku: sku })
        } catch {
          /* local override still applied */
        }
      }
      await queryClient.invalidateQueries({ queryKey: ['product_modules'] })
      await queryClient.invalidateQueries({ queryKey: ['config'] })
    },
    [isSuperAdmin, queryClient]
  )

  const clearSkuOverride = useCallback(
    async (clearApi = false) => {
      writeLocalSkuOverride(null)
      setLocalSku(null)
      if (clearApi && isSuperAdmin) {
        try {
          await AXIOS_INSTANCE.delete('/product_modules')
        } catch {
          /* ignore */
        }
      }
      await queryClient.invalidateQueries({ queryKey: ['product_modules'] })
      await queryClient.invalidateQueries({ queryKey: ['config'] })
    },
    [isSuperAdmin, queryClient]
  )

  return {
    productSku,
    apiProductSku,
    enabledModules,
    apiEnabledModules,
    isModuleEnabled,
    isSuperAdmin,
    bypassModuleGates,
    isLoading,
    localSku,
    availableSkus: (data?.available_skus as ProductSkuId[]) || (Object.keys(PRODUCT_SKUS) as ProductSkuId[]),
    setSkuOverride,
    clearSkuOverride,
    refetch
  }
}
